/**
 * A/B Test Purchase Widget V2
 *
 * Subscribe-first design: subscription is the default expanded state.
 * "Or buy one-time" collapses to a minimal one-time card (Option A).
 * Syncs with legacy hidden radios (#one_time / #subscr) for js_product.js compat.
 */

window.ABTestPurchaseWidgetV2 = window.ABTestPurchaseWidgetV2 || class ABTestPurchaseWidgetV2 {
  constructor() {
    this.widget = null;
    this.isInitialized = false;
    this.isSubscriptionMode = true; // subscribe is default

    this.handleOneTimeClick = this.handleOneTimeClick.bind(this);
    this.handleSwitchToSub = this.handleSwitchToSub.bind(this);
    this.handleFrequencyChange = this.handleFrequencyChange.bind(this);
    this.updatePriceDisplay = this.updatePriceDisplay.bind(this);

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupWidget());
    } else {
      this.setupWidget();
    }
  }

  setupWidget() {
    this.widget = document.getElementById('abtest-v2-purchase-widget');
    if (!this.widget) return;

    this.subCard = document.getElementById('abtest-v2-sub-card');
    this.subBody = document.getElementById('abtest-v2-sub-body');
    this.onetimeRow = document.getElementById('abtest-v2-onetime-row');
    this.onetimeCard = document.getElementById('abtest-v2-onetime-card');
    this.onetimeToggle = document.getElementById('abtest-v2-onetime-toggle');
    this.switchToSub = document.getElementById('abtest-v2-switch-to-sub');
    this.frequencySelect = document.getElementById('abtest-v2-frequency-select');

    this.pricingMap = this.parseJSON('[data-variant-pricing-map]');
    this.planMap = this.parseJSON('[data-variant-plan-map]');

    this.bindEventListeners();
    this.setInitialState();
    this.isInitialized = true;
  }

  parseJSON(selector) {
    const el = this.widget.querySelector(selector);
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  bindEventListeners() {
    if (this.onetimeToggle) {
      this.onetimeToggle.addEventListener('click', this.handleOneTimeClick);
    }
    if (this.switchToSub) {
      this.switchToSub.addEventListener('click', this.handleSwitchToSub);
    }
    if (this.frequencySelect) {
      this.frequencySelect.addEventListener('change', this.handleFrequencyChange);
    }

    // Listen for variant changes
    document.addEventListener('change', (e) => {
      if (e.target.closest('variant-picker') || e.target.matches('[name="id"]')) {
        setTimeout(() => {
          this.syncSubscriptionAvailability();
          this.updatePriceDisplay();
        }, 100);
      }
    });
  }

  // ─── State transitions ───

  handleOneTimeClick(e) {
    e.preventDefault();
    this.setMode('onetime');
  }

  handleSwitchToSub(e) {
    e.preventDefault();
    this.setMode('subscribe');
  }

  setMode(mode) {
    this.isSubscriptionMode = mode === 'subscribe';

    if (this.isSubscriptionMode) {
      // Show subscribe card expanded
      this.subCard.classList.remove('abtest-v2-card--collapsed');
      this.subCard.classList.add('abtest-v2-card--active');
      this.subBody.classList.remove('collapsed');
      this.subCard.querySelector('.abtest-v2-radio-indicator').classList.add('abtest-v2-radio-indicator--active');
      // Show badges
      this.subCard.querySelectorAll('.abtest-v2-badge').forEach(b => b.style.display = '');
      // Show price original + sub price
      const priceOrig = this.subCard.querySelector('.abtest-v2-price-original');
      if (priceOrig) priceOrig.style.display = '';

      // Show "Or buy one-time" link, hide one-time card
      // OTP elements are absent when product.requires_selling_plan is true (subonly products)
      if (this.onetimeRow) {
        this.onetimeRow.classList.remove('hidden');
        this.onetimeRow.style.display = '';
      }
      if (this.onetimeCard) this.onetimeCard.classList.add('hidden');

      // Sync hidden form
      this.updateHiddenPurchaseOptions(true);
      const planId = this.frequencySelect ? this.frequencySelect.value : '';
      this.updateHiddenSellingPlan(planId);
    } else {
      // Collapse subscribe card
      this.subCard.classList.add('abtest-v2-card--collapsed');
      this.subCard.classList.remove('abtest-v2-card--active');
      this.subBody.classList.add('collapsed');
      this.subCard.querySelector('.abtest-v2-radio-indicator').classList.remove('abtest-v2-radio-indicator--active');
      // Keep all badges + compare-at price visible when collapsed so the user sees the full discount framing in both modes

      // Hide "Or buy one-time" link, show one-time card
      if (this.onetimeRow) this.onetimeRow.style.display = 'none';
      if (this.onetimeCard) {
        this.onetimeCard.classList.remove('hidden');
        // Activate one-time radio dot
        const otRadio = this.onetimeCard.querySelector('.abtest-v2-radio-indicator');
        if (otRadio) otRadio.classList.add('abtest-v2-radio-indicator--active');
      }

      // Sync hidden form
      this.updateHiddenPurchaseOptions(false);
      this.updateHiddenSellingPlan('');
    }

    // Let collapsed subscribe card act as a click target to re-enable subscription
    if (!this.isSubscriptionMode) {
      this.subCard.style.cursor = 'pointer';
      this._subCardClickHandler = this._subCardClickHandler || ((e) => {
        // Only re-enable if we're in one-time mode and clicking the collapsed card
        if (!this.isSubscriptionMode && !e.target.closest('select') && !e.target.closest('button')) {
          this.setMode('subscribe');
        }
      });
      this.subCard.addEventListener('click', this._subCardClickHandler);
    } else {
      this.subCard.style.cursor = '';
    }

    this.updatePriceDisplay();
    this.updateButtonLabel();
    this.updateBelowCTA();
  }

  handleFrequencyChange(e) {
    this.updateHiddenSellingPlan(e.target.value);
    // Also update legacy hidden select
    const legacySelect = document.getElementById('subscr_select');
    if (legacySelect) legacySelect.value = e.target.value;
  }

  // ─── Price & label updates ───

  updatePriceDisplay() {
    const variantInput = document.querySelector('.sellingPlanVarID');
    const variantId = variantInput ? variantInput.value : null;
    if (!variantId || !this.pricingMap[variantId]) return;

    const data = this.pricingMap[variantId];
    const unitPrices = window._igOriginalPrices || {};
    const unitOneTime = typeof unitPrices.oneTime === 'number' ? unitPrices.oneTime : null;
    const unitSub = typeof unitPrices.subscription === 'number' ? unitPrices.subscription : null;

    let onetimeCents, subCents;
    if (unitOneTime !== null && unitSub !== null) {
      onetimeCents = Math.round(unitOneTime * 100);
      subCents = Math.round(unitSub * 100);
    } else {
      onetimeCents = data.price || 0;
      subCents = data.subscription_price || data.price || 0;
    }

    // Update subscribe card prices (use compare-at price for strikethrough if available)
    const compareAtCents = data.compare_at_price || null;
    if (this.subCard) {
      const origPrice = this.subCard.querySelector('.abtest-v2-price-original:not([data-lp-compare]) .money');
      if (origPrice) origPrice.textContent = this.formatPrice(compareAtCents || onetimeCents);

      const subPrice = this.subCard.querySelector('.abtest-v2-price-sub');
      if (subPrice) subPrice.textContent = this.formatPrice(subCents);
    } else {
      // No-subscription fallback card (no subscribe card present): update its
      // one-time price + compare-at strikethrough.
      const otOnlyCard = document.getElementById('abtest-v2-onetime-only-card');
      if (otOnlyCard) {
        const otOnlyOrig = otOnlyCard.querySelector('.abtest-v2-price-original .money');
        if (otOnlyOrig) otOnlyOrig.textContent = this.formatPrice(compareAtCents || onetimeCents);
        const otOnlyPrice = otOnlyCard.querySelector('.abtest-v2-price-sub');
        if (otOnlyPrice) otOnlyPrice.textContent = this.formatPrice(onetimeCents);
      }
    }

    // Update one-time link price
    const otLinkPrice = this.widget.querySelector('.abtest-v2-onetime-price');
    if (otLinkPrice) otLinkPrice.textContent = this.formatPrice(onetimeCents);

    // Update one-time card price (target inner span to preserve sibling compare-at strikethrough)
    const otCardPrice = this.widget.querySelector('.abtest-v2-onetime-price-main')
      || this.widget.querySelector('.abtest-v2-onetime-price-display');
    if (otCardPrice) otCardPrice.textContent = this.formatPrice(onetimeCents);
  }

  updateButtonLabel() {
    const label = document.querySelector('.original_label[data-abtest-v2="true"]');
    if (!label) return;

    const variantInput = document.querySelector('.sellingPlanVarID');
    const variantId = variantInput ? variantInput.value : null;
    const data = variantId && this.pricingMap[variantId] ? this.pricingMap[variantId] : null;

    const unitPrices = window._igOriginalPrices || {};
    let priceCents;
    if (this.isSubscriptionMode) {
      priceCents = typeof unitPrices.subscription === 'number'
        ? Math.round(unitPrices.subscription * 100)
        : (data ? (data.subscription_price || data.price) : 0);
    } else {
      priceCents = typeof unitPrices.oneTime === 'number'
        ? Math.round(unitPrices.oneTime * 100)
        : (data ? data.price : 0);
    }

    const priceStr = this.formatPrice(priceCents);
    // S&C premium lander: fixed label from the widget (data-cta-label).
    const ctaLabel = this.widget && this.widget.dataset.ctaLabel;
    if (ctaLabel) {
      if (window.lpSetCta) window.lpSetCta(label, priceStr);
      else label.textContent = ctaLabel + ' \u00b7 ' + priceStr;
      return;
    }
    if (this.isSubscriptionMode) {
      label.textContent = 'Subscribe & Save - ' + priceStr;
    } else {
      label.textContent = 'Add to cart - ' + priceStr;
    }
  }

  updateBelowCTA() {
    const belowCta = document.getElementById('abtest-v2-below-cta');
    if (!belowCta) return;

    // If the widget emits a selling-plan cadence (pdp-2026-free-gift), render the
    // dynamic "Refill every X. Cancel anytime" copy below the CTA. Falls back to
    // whatever static copy was rendered server-side if no cadence is available.
    const cadence = this.widget && this.widget.dataset.cadence;
    if (cadence) {
      belowCta.textContent = 'Refill every ' + cadence + '. Cancel anytime';
      belowCta.setAttribute('data-cadence-applied', cadence);
    }

    if (this.isSubscriptionMode) {
      belowCta.style.display = '';
    } else {
      belowCta.style.display = 'none';
    }
  }

  // ─── Subscription availability sync ───

  syncSubscriptionAvailability() {
    // No subscribe card (no-subscription product) → nothing to sync.
    if (!this.subCard) return;
    const subscrInput = document.getElementById('subscr');
    const subscriptionAvailable = subscrInput ? !subscrInput.disabled : true;
    const requiresPlan = this.widget && this.widget.dataset.requiresPlan === 'true';

    if (!subscriptionAvailable && !requiresPlan) {
      // Force one-time mode, disable subscribe card
      this.setMode('onetime');
      this.subCard.style.opacity = '0.5';
      this.subCard.style.pointerEvents = 'none';
      if (this.onetimeToggle) this.onetimeToggle.style.display = 'none';
    } else {
      this.subCard.style.opacity = '';
      this.subCard.style.pointerEvents = '';
      if (this.onetimeToggle) this.onetimeToggle.style.display = '';
    }
  }

  // ─── Hidden form sync (legacy compat) ───

  updateHiddenPurchaseOptions(isSubscription) {
    const subscriptionRadio = document.getElementById('subscr');
    const oneTimeRadio = document.getElementById('one_time');

    if (isSubscription && subscriptionRadio && !subscriptionRadio.checked) {
      subscriptionRadio.checked = true;
      subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!isSubscription && oneTimeRadio && !oneTimeRadio.checked) {
      oneTimeRadio.checked = true;
      oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateHiddenSellingPlan(planId) {
    const sellingPlanInput = document.querySelector('[name="selling_plan"]');
    const hiddenSelector = document.getElementById('subscr_select');

    if (sellingPlanInput) sellingPlanInput.value = planId || '';
    if (hiddenSelector) hiddenSelector.value = planId || '';
  }

  // ─── Initial state ───

  setInitialState() {
    // No subscribe card present → no-subscription product (e.g. limited edition
    // on pdp-2026-free-gift). Run as a pure one-time / "Add to cart" widget:
    // no subscribe default, no mode toggle, no sub subtext.
    if (!this.subCard) {
      this.isSubscriptionMode = false;
      this.updatePriceDisplay();
      this.updateButtonLabel();
      this.updateBelowCTA();
      return;
    }

    // Subscribe is default — set hidden form values
    const subscrRadio = document.getElementById('subscr');
    if (subscrRadio) {
      subscrRadio.checked = true;
      subscrRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Set selling plan from frequency dropdown
    if (this.frequencySelect && this.frequencySelect.value) {
      this.updateHiddenSellingPlan(this.frequencySelect.value);
    }

    this.syncSubscriptionAvailability();
    this.updatePriceDisplay();
    this.updateButtonLabel();
    this.updateBelowCTA();
  }

  // ─── Utilities ───

  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  }

  refresh() {
    if (this.isInitialized) {
      this.updatePriceDisplay();
      this.updateButtonLabel();
    }
  }
};

// Initialize
function initializeABTestWidgetV2() {
  if (document.getElementById('abtest-v2-purchase-widget')) {
    if (!window.ABTestWidgetV2 || !window.ABTestWidgetV2.isInitialized) {
      window.ABTestWidgetV2 = new window.ABTestPurchaseWidgetV2();
    } else {
      try { window.ABTestWidgetV2.refresh(); } catch (e) {}
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeABTestWidgetV2);
} else {
  initializeABTestWidgetV2();
}

document.addEventListener('content:loaded', initializeABTestWidgetV2);
